/**
 * Host-page attribution, shared by every hosted-iframe loader.
 *
 * A loader runs in the HOST page (maxretail.com), while the widget it embeds
 * runs on our own domain inside the iframe. The widget therefore cannot read
 * the host's `_ga` / `_gcl_aw` cookies, and without help GA4 would count every
 * lead as a brand-new user with no campaign attribution. The loader reads them
 * here and forwards them as query params, which the widget reads back via
 * `resolveGaClientId` / `resolveGclid` in `analytics.ts`.
 *
 * Extracted from `iframe.ts` so the payout-calculator popup stitches identity
 * exactly the same way rather than growing a second, subtly different copy.
 */
import { UTM_PARAMS, normalizeGaClientId, normalizeGclid } from './analytics.js'

/** Read a raw cookie value by name, or '' when absent. */
export const readCookie = (name: string): string => {
  const prefix = `${name}=`
  return (
    document.cookie
      .split('; ')
      .find((c) => c.startsWith(prefix))
      ?.slice(prefix.length) ?? ''
  )
}

/** How long a captured UTM value survives before a fresh ad click is needed. */
const UTM_COOKIE_MAX_AGE_DAYS = 90
/** Cookie name for a given UTM param, kept distinct from HubSpot/GA property names. */
const utmCookieName = (param: string): string => `mr_${param}`

const writeCookie = (name: string, value: string, maxAgeDays: number): void => {
  const maxAge = maxAgeDays * 24 * 60 * 60
  // CookieStore isn't available in every browser this widget needs to run in
  // yet; the values written here are non-sensitive marketing params.
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
}

/**
 * Read a value written by `writeCookie` back out, undoing its
 * `encodeURIComponent`. `readCookie` alone isn't enough here — a raw
 * `utm_term=shoe%20boutique` would otherwise round-trip through the iframe
 * URL still percent-encoded and land in HubSpot as the literal text
 * "shoe%20boutique" instead of "shoe boutique".
 */
const readEncodedCookie = (name: string): string => {
  const raw = readCookie(name)
  if (!raw) {
    return ''
  }
  try {
    return decodeURIComponent(raw)
  } catch {
    return ''
  }
}

/**
 * Persist any UTM params present on the CURRENT page's URL into first-party
 * cookies (unlike `gclid`, nothing else on the site does this automatically).
 * Needed because an ad can land on a page other than the one that embeds the
 * lead-form widget — e.g. the homepage — and in-site navigation to that page
 * typically drops the query string. Call this on every page of the host
 * site, not just the one with the widget (see `utm-capture.ts`).
 *
 * Last-touch: a visit that carries a param overwrites the stored value; a
 * visit with no params leaves whatever was already stored alone.
 */
export const captureUtmParams = (): void => {
  const params = new URLSearchParams(window.location.search)
  for (const name of Object.values(UTM_PARAMS)) {
    const value = params.get(name)
    if (value) {
      writeCookie(utmCookieName(name), value, UTM_COOKIE_MAX_AGE_DAYS)
    }
  }
}

/** GA4's `client_id` — the trailing `<random>.<timestamp>` of the `_ga` cookie. */
export const hostGaClientId = (): string =>
  normalizeGaClientId(readCookie('_ga'))

/**
 * Google Ads click id — from the landing URL's `?gclid=` (present when this
 * pageview IS the ad click), else unwrapped from the `_gcl_aw` cookie a Google
 * tag set earlier in the session.
 *
 * Unlike the client id there is deliberately no fallback: a made-up value would
 * falsely attribute an organic lead to a paid ad.
 */
export const hostGclid = (): string => {
  const fromParam = new URLSearchParams(window.location.search).get('gclid')
  return normalizeGclid(fromParam) || normalizeGclid(readCookie('_gcl_aw'))
}

/**
 * Copy the host page's attribution onto a widget URL. Params are only set when
 * a value was actually found, so the widget can tell "absent" from "empty".
 *
 * UTM params fall back to the `captureUtmParams` cookie when the current
 * page's URL doesn't have them — the widget's own embed page is often not
 * the page the ad landed on. Also captures the current page's params first,
 * so a lead form embedded directly on the landing page still works even if
 * the host site hasn't added the sitewide capture script yet.
 */
export const withAttribution = (url: URL): URL => {
  captureUtmParams()
  const cid = hostGaClientId()
  if (cid) {
    url.searchParams.set('ga_cid', cid)
  }
  const gclid = hostGclid()
  if (gclid) {
    url.searchParams.set('gclid', gclid)
  }
  for (const name of Object.values(UTM_PARAMS)) {
    const value = readEncodedCookie(utmCookieName(name))
    if (value) {
      url.searchParams.set(name, value)
    }
  }
  return url
}
